import { Box, styled } from "@mui/material";
import React, { useEffect, useState } from "react";
import { CRICKET_BULL_ASP, CRICKET_MARK_ASP } from "../../types/dart";
import { cloneDeep } from "lodash";
import { initPlayerResultASPs, initResults } from "../../util/cricketLogic";
import { BaseMatchState } from "../../types/match";
import { CricketGameState, CricketResults } from "../../types/cricket";
import { TEAM } from "../../types/teams";

interface CricketASPTableProps {
  matchState: BaseMatchState;
}

export default function CricketASPTable({ matchState }: CricketASPTableProps) {
  const [results, setResults] = useState<CricketResults>(
    cloneDeep(initResults)
  );
  const lastLeg: CricketGameState | undefined =
    (matchState.completed.legs[
      matchState.completed.legs.length - 1
    ] as CricketGameState) ??
    (matchState.completed.sets[matchState.completed.sets.length - 1]?.legs[
      matchState.completed.sets[matchState.completed.sets.length - 1].legs
        .length - 1
    ] as CricketGameState);

  useEffect(() => {
    if (!lastLeg) return;
    const newASPs = cloneDeep(initResults);
    Object.entries(results).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (const player of lastLeg[team].players) {
        newASPs[team].push({
          leg: {
            ...player.asps,
            total: {
              throws: player.totalThrows,
              marks: player.totalMarks,
            },
          },
          match: cloneDeep(initPlayerResultASPs),
        });
      }
    });
    for (const leg of matchState.completed.legs) {
      setLegASPs(newASPs, leg as CricketGameState);
    }
    for (const set of matchState.completed.sets) {
      for (const leg of set.legs) {
        setLegASPs(newASPs, leg as CricketGameState);
      }
    }
    setResults(newASPs);
    // eslint-disable-next-line
  }, [matchState]);

  const setLegASPs = (newAsps: CricketResults, leg: CricketGameState) => {
    Object.entries(newAsps).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (let i = 0; i < newAsps[team].length; i++) {
        const player = newAsps[team][i];
        const legASP = leg[team].players[i].asps;
        player.match.total.throws += leg[team].players[i].totalThrows;
        player.match.total.marks += leg[team].players[i].totalMarks;

        Object.entries(legASP.marks).forEach(([key, value]) => {
          const asp: CRICKET_MARK_ASP = Object.values(CRICKET_MARK_ASP).find(
            (x) => (x as CRICKET_MARK_ASP) === Number(key)
          ) as CRICKET_MARK_ASP;
          player.match.marks[asp] += value;
        });
        Object.entries(legASP.bulls).forEach(([key, value]) => {
          const asp: CRICKET_BULL_ASP = Object.values(CRICKET_BULL_ASP).find(
            (x) => (x as CRICKET_BULL_ASP) === Number(key)
          ) as CRICKET_BULL_ASP;
          player.match.bulls[asp] += value;
        });
      }
    });
  };

  const getRoundMarks = (total: { marks: number; throws: number }): string => {
    const formatter = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
    });
    const avg = total.marks / (total.throws / 3);
    return formatter.format(avg);
  };

  const getTotalThrowsRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player = results.team1[i];
      tds.push(
        <DataCell key={`team1_${i}_leg_totalThrows`}>
          {player.leg.total.throws}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team1_${i}_match_totalThrows`}>
          {player.match.total.throws}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`totalThrows`}>Total Throws</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player = results.team2[i];
      tds.push(
        <DataCell key={`team2_${i}_leg_totalThrows`}>
          {player.leg.total.throws}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team2_${i}_match_totalThrows`}>
          {player.match.total.throws}
        </DataCell>
      );
    }

    return <TableRow key={"totalThrowsRow"}>{tds}</TableRow>;
  };

  const getPerRoundRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player = results.team1[i];
      tds.push(
        <DataCell key={`team1_${i}_leg_mpr`}>
          {getRoundMarks(player.leg.total)}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team1_${i}_match_mpr`}>
          {getRoundMarks(player.match.total)}
        </DataCell>
      );
    }
    tds.push(<DataCell key={`mpr`}>Marks Per Round</DataCell>);
    for (let i = 0; i < results.team2.length; i++) {
      const player = results.team2[i];
      tds.push(
        <DataCell key={`team2_${i}_leg_mpr`}>
          {getRoundMarks(player.leg.total)}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team2_${i}_match_mpr`}>
          {getRoundMarks(player.match.total)}
        </DataCell>
      );
    }

    return <TableRow key={"mprRow"}>{tds}</TableRow>;
  };

  function showMarkASP(asp: CRICKET_MARK_ASP): boolean {
    let showASP = false;
    Object.entries(results).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (const player of results[team]) {
        if (player.match.marks[asp] > 0) {
          showASP = true;
        }
      }
    });
    return showASP;
  }

  const getASPMarkRow = (asp: CRICKET_MARK_ASP): JSX.Element | null => {
    if (!showMarkASP(asp)) return null;
    const tds: JSX.Element[] = [];

    for (let i = 0; i < results.team1.length; i++) {
      const player = results.team1[i];
      tds.push(
        <DataCell key={`$team1_${i}_leg_${asp}_mark`}>
          {player.leg.marks[asp]}
        </DataCell>
      );
      tds.push(
        <DataCell key={`$team1_${i}_match_${asp}_mark`}>
          {player.match.marks[asp]}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`${asp}_mark`}>{asp} Mark</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player = results.team2[i];
      tds.push(
        <DataCell key={`team2_${i}_leg_${asp}_mark`}>
          {player.leg.marks[asp]}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team2_${i}_match_${asp}_mark`}>
          {player.match.marks[asp]}
        </DataCell>
      );
    }

    return <TableRow key={`${asp}_mark_row`}>{tds}</TableRow>;
  };

  function showBullASP(asp: CRICKET_BULL_ASP): boolean {
    let showASP = false;
    Object.entries(results).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (const player of results[team]) {
        if (player.match.bulls[asp] > 0) {
          showASP = true;
        }
      }
    });
    return showASP;
  }

  const getASPBullRow = (asp: CRICKET_BULL_ASP): JSX.Element | null => {
    if (!showBullASP(asp)) return null;
    const tds: JSX.Element[] = [];

    for (let i = 0; i < results.team1.length; i++) {
      const player = results.team1[i];
      tds.push(
        <DataCell key={`$team1_${i}_leg_${asp}_bull`}>
          {player.leg.bulls[asp]}
        </DataCell>
      );
      tds.push(
        <DataCell key={`$team1_${i}_match_${asp}_bull`}>
          {player.match.bulls[asp]}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`${asp}_bull`}>{asp} Bull</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player = results.team2[i];
      tds.push(
        <DataCell key={`$team2_${i}_leg_${asp}_bull`}>
          {player.leg.bulls[asp]}
        </DataCell>
      );
      tds.push(
        <DataCell key={`$team2_${i}_match_${asp}_bull`}>
          {player.match.bulls[asp]}
        </DataCell>
      );
    }

    return <TableRow key={`${asp}bull_row`}>{tds}</TableRow>;
  };

  return (
    <Box alignSelf="center">
      <Table>
        <thead>
          <tr>
            {lastLeg.team1.players.map((player) => (
              <HeaderCell key={player.name} colSpan={2}>
                {player.name}
              </HeaderCell>
            ))}
            <HeaderCell colSpan={1} />
            {lastLeg.team2.players.map((player) => (
              <HeaderCell key={player.name} colSpan={2}>
                {player.name}
              </HeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableRow>
            {lastLeg.team1.players.map((_, i) => (
              <React.Fragment key={`team1_${i}_leg_match_row`}>
                <DataCell>Leg</DataCell>
                <DataCell>Match</DataCell>
              </React.Fragment>
            ))}
            <DataCell></DataCell>
            {lastLeg.team2.players.map((_, i) => (
              <React.Fragment key={`team2_${i}_leg_match_row`}>
                <DataCell>Leg</DataCell>
                <DataCell>Match</DataCell>
              </React.Fragment>
            ))}
          </TableRow>
          {getTotalThrowsRow()}
          {getPerRoundRow()}
          {Object.entries(initPlayerResultASPs.marks).map((marks: any) => {
            const mark: CRICKET_MARK_ASP = marks[0] as CRICKET_MARK_ASP;
            return getASPMarkRow(mark);
          })}
          {Object.entries(initPlayerResultASPs.bulls).map((bulls: any) => {
            const bull: CRICKET_BULL_ASP = bulls[0] as CRICKET_BULL_ASP;
            return getASPBullRow(bull);
          })}
        </tbody>
      </Table>
    </Box>
  );
}

const Table = styled("table")({
  borderCollapse: "collapse",
});

const TableRow = styled("tr")({
  borderBottom: "4px double black",
});

const HeaderCell = styled("th")({
  padding: "5px",
});

const DataCell = styled("td")({
  padding: "5px",
});
